import React, { useCallback, useEffect, useRef, useState } from "react";

const TERMINAL = new Set(["COMPLETED", "PARTIAL_SUCCESS", "FAILED", "NO_ELIGIBLE_CLIENTS", "RECONCILIATION_REQUIRED", "CANCELLED"]);
const SUCCESS = new Set(["PLACED", "DUMMY", "COMPLETED", "SUCCESS", "FILLED"]);
const FAILED = new Set(["FAILED", "REJECTED", "CANCELLED", "RECONCILIATION_REQUIRED"]);
const money = value => Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const isAutoIndexOrder = order => {
  const family = String(order?.strategy_family || "").toUpperCase();
  const product = String(order?.product_id || "").toUpperCase();
  return String(order?.source || "").toUpperCase() === "ALGO"
    && (family.startsWith("INDEXOPTION_") || product.startsWith("AUTO_INDEX_") || product.startsWith("INDEXOPTION_"));
};

const orderKind = order => String(order?.source || "").toUpperCase() === "AUTO_STOCK"
  ? "AUTO_STOCK"
  : isAutoIndexOrder(order) ? "AUTO_INDEX" : null;

export default function TradeNotificationCenter({ hydrated = false, orders = [], autoStock = {}, indexInstances = {}, onNavigate }) {
  const supported = typeof window !== "undefined" && "Notification" in window;
  const [enabled, setEnabled] = useState(() => window.localStorage.getItem("mintx.tradeAlerts") === "enabled");
  const [permission, setPermission] = useState(() => supported ? window.Notification.permission : "unsupported");
  const [popups, setPopups] = useState([]);
  const audioRef = useRef(null);
  const popupTimersRef = useRef(new Map());
  const orderBaselineRef = useRef(false);
  const alignmentBaselineRef = useRef(false);
  const seenOrdersRef = useRef(new Set());
  const seenAlignmentsRef = useRef(new Set());

  const playSound = useCallback((tone = "normal") => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      const context = audioRef.current || new AudioContext();
      audioRef.current = context;
      if (context.state === "suspended") context.resume().catch(() => {});
      const frequencies = tone === "warning" ? [330, 220, 330] : tone === "entry" ? [660, 880] : tone === "alignment" ? [520] : [740, 560];
      frequencies.forEach((frequency, index) => {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        const start = context.currentTime + index * 0.16;
        oscillator.frequency.value = frequency;
        oscillator.type = "sine";
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.12, start + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.13);
        oscillator.connect(gain).connect(context.destination);
        oscillator.start(start);
        oscillator.stop(start + 0.14);
      });
    } catch {
      // Native system notification sound remains available.
    }
  }, []);

  const dismissPopup = useCallback(id => {
    const timer = popupTimersRef.current.get(id);
    if (timer) window.clearTimeout(timer);
    popupTimersRef.current.delete(id);
    setPopups(current => current.filter(item => item.id !== id));
  }, []);

  const showPopup = useCallback((title, body, tag, tone, destination) => {
    const id = `${tag}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setPopups(current => [...current.slice(-3), { id, title, body, tone, destination }]);
    const timer = window.setTimeout(() => {
      popupTimersRef.current.delete(id);
      setPopups(current => current.filter(item => item.id !== id));
    }, 9000);
    popupTimersRef.current.set(id, timer);
  }, []);

  useEffect(() => () => {
    popupTimersRef.current.forEach(timer => window.clearTimeout(timer));
    popupTimersRef.current.clear();
  }, []);

  const notify = useCallback((title, body, tag, tone, destination) => {
    if (!enabled) return;
    playSound(tone);
    showPopup(title, body, tag, tone, destination);
    if (!supported || permission !== "granted") return;
    try {
      const notification = new window.Notification(title, {
        body, tag, icon: "/brand/mintxpro-logo-cyan-emerald.png", silent: false,
      });
      notification.onclick = () => {
        window.focus();
        onNavigate?.(destination);
        notification.close();
      };
    } catch {
      // The in-app popup remains visible if the OS/browser blocks banners.
    }
  }, [enabled, permission, playSound, showPopup, supported, onNavigate]);

  const toggle = async () => {
    if (supported && window.Notification.permission !== "granted") {
      const result = await window.Notification.requestPermission();
      setPermission(result);
      if (result !== "granted") return;
      setEnabled(true);
      window.localStorage.setItem("mintx.tradeAlerts", "enabled");
      playSound("entry");
      new window.Notification("MintXPro trade alerts enabled", {
        body: "Auto Stock and Auto Index alignment, entry, exit and failure alerts are active.",
        tag: "mintx-alerts-enabled", icon: "/brand/mintxpro-logo-cyan-emerald.png",
      });
      return;
    }
    const next = !enabled;
    setEnabled(next);
    window.localStorage.setItem("mintx.tradeAlerts", next ? "enabled" : "muted");
    if (next) playSound("entry");
  };

  useEffect(() => {
    if (!hydrated) return;
    const relevant = orders.filter(order => orderKind(order) && TERMINAL.has(String(order.status || "").toUpperCase()));
    if (!orderBaselineRef.current) {
      relevant.forEach(order => seenOrdersRef.current.add(String(order.order_id)));
      orderBaselineRef.current = true;
      return;
    }
    relevant.forEach(order => {
      const id = String(order.order_id || "");
      if (!id || seenOrdersRef.current.has(id)) return;
      seenOrdersRef.current.add(id);
      const kind = orderKind(order);
      const clients = (Array.isArray(order.clients) ? order.clients : []).filter(row => row.client_id !== "admin");
      const success = clients.filter(row => SUCCESS.has(String(row.status || "").toUpperCase()));
      const failed = clients.filter(row => FAILED.has(String(row.status || "").toUpperCase()));
      const skipped = clients.filter(row => String(row.status || "").toUpperCase() === "SKIPPED");
      const prices = success.map(row => Number(row.average_price || row.fill_price || row.price || 0)).filter(value => value > 0);
      const quantities = success.reduce((sum, row) => sum + Math.abs(Number(row.filled_qty || row.quantity || row.qty || 0)), 0);
      const priceText = prices.length ? `₹${money(Math.min(...prices))}${Math.max(...prices) !== Math.min(...prices) ? `–₹${money(Math.max(...prices))}` : ""}` : "price unavailable";
      const action = order.is_square_off ? "Exit" : "Entry";
      const product = kind === "AUTO_STOCK" ? "Auto Stock" : "Auto Index";
      const reason = order.exit_reason || order.signal_reason || order.reason || "Strategy rule";
      const body = `${order.side || ""} ${order.tradingsymbol || order.underlying || ""} · ${priceText} · Qty ${quantities || "—"}\nClients: ${success.length} completed, ${failed.length} failed, ${skipped.length} skipped\n${action === "Exit" ? `Reason: ${reason}` : `Signal: ${reason}`}`;
      notify(`${product} ${action} ${failed.length ? "· Attention" : "· Executed"}`, body, `mintx-order-${id}`, failed.length ? "warning" : action === "Entry" ? "entry" : "exit", kind);
      if (failed.length) {
        const names = failed.slice(0, 4).map(row => row.client_id || row.client_name).filter(Boolean).join(", ");
        notify(`${product} broker failure`, `${order.tradingsymbol}: ${failed.length} client order(s) failed${names ? ` · ${names}` : ""}. Open MintXPro for details.`, `mintx-failure-${id}`, "warning", kind);
      }
    });
  }, [hydrated, orders, notify]);

  useEffect(() => {
    if (!hydrated) return;
    const candidates = [];
    (autoStock.stocks || []).forEach(row => {
      if (!autoStock.running || !row.manual_entry_side || !row.validated_candle_time) return;
      candidates.push({
        key: `stock:${row.symbol}:${row.manual_entry_side}:${row.validated_candle_time}`,
        title: `Auto Stock ${row.manual_entry_side} alignment · ${row.symbol}`,
        body: `Previous 5-minute candle · ST ${row.supertrend} · EMA5 ${money(row.ema5)} ${row.manual_entry_side === "BUY" ? ">" : "<"} EMA11 ${money(row.ema11)}\nLive LTP must break ${row.manual_entry_side === "BUY" ? `previous high ${money(row.conditions?.previous_high)}` : `previous low ${money(row.conditions?.previous_low)}`}. Entry is eligible from 09:20.`,
        destination: "AUTO_STOCK",
      });
    });
    Object.entries(indexInstances || {}).forEach(([instrument, state]) => {
      const setup = state?.manual_entry || {};
      if (!state?.running || !setup.available || !setup.candle_time || !setup.side) return;
      const choice = setup.choices?.[setup.side] || {};
      const rules = choice.rules || setup.rules || {};
      const indexRules = Object.entries(rules).filter(([name, rule]) => name.startsWith("index_") && rule?.enabled !== false);
      const indexAligned = indexRules.length > 0 && indexRules.every(([, rule]) => Boolean(rule?.passed));
      if (!indexAligned) return;
      const snap = state.index_snapshot || {};
      const option = state.option_snapshots?.[setup.side] || choice;
      const complete = !setup.manual_override && (choice.aligned !== false);
      candidates.push({
        key: `index:${instrument}:${setup.side}:${setup.candle_time}:${complete ? "complete" : "index"}`,
        title: complete ? `${instrument} ${setup.side} setup fully aligned` : `${instrument} aligned · option confirmation pending`,
        body: `Spot ${money(snap.ltp)} · ST ${snap.supertrend || "—"} · EMA5 ${money(snap.ema5)} / EMA13 ${money(snap.ema13)} · Gap ${money(snap.ema_gap)}\n${option.symbol || setup.symbol || setup.side} · LTP ₹${money(option.ltp)} · ${complete ? "Index and option conditions valid" : `${choice.failed_rules?.length || setup.failed_rules?.length || 0} option/setup checks pending`}`,
        destination: "AUTO_INDEX",
      });
    });
    if (!alignmentBaselineRef.current) {
      candidates.forEach(item => seenAlignmentsRef.current.add(item.key));
      alignmentBaselineRef.current = true;
      return;
    }
    candidates.forEach(item => {
      if (seenAlignmentsRef.current.has(item.key)) return;
      seenAlignmentsRef.current.add(item.key);
      notify(item.title, item.body, `mintx-${item.key}`, "alignment", item.destination);
    });
  }, [hydrated, autoStock, indexInstances, notify]);

  return <>
    <div className="trade-alert-controls">
      <button className={enabled ? "trade-alert-on" : ""} onClick={toggle} title="Auto Stock and Auto Index notifications">
        {enabled ? "Alerts On" : permission !== "granted" && supported ? "Enable Alerts" : "Alerts Muted"}
      </button>
      {enabled && <button onClick={() => notify("MintXPro alert test", "In-app popup, system notification and sound are working.", `mintx-test-${Date.now()}`, "entry", "AUTO_STOCK")}>Test</button>}
    </div>
    <div className="trade-popup-stack" aria-live="assertive" aria-label="Trade notifications">
      {popups.length > 1 && <div className="trade-popup-more">+{popups.length - 1} earlier alert{popups.length === 2 ? "" : "s"}</div>}
      {popups.map(item => <article key={item.id} className={`trade-popup trade-popup-${item.tone || "normal"}`}>
        <button className="trade-popup-content" onClick={() => { onNavigate?.(item.destination); dismissPopup(item.id); }}>
          <strong>{item.title}</strong><span>{item.body}</span>
        </button>
        <button className="trade-popup-close" aria-label="Dismiss notification" onClick={() => dismissPopup(item.id)}>×</button>
      </article>)}
    </div>
  </>;
}
